import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Text, Input, Password, Button, Link } from 'hoi-poi-ui';
import { PasswordPolicies } from '@web/web5';
import * as Tracking from 'utils/tracking';
import Context from 'managers/Context';
import { REG_EX_EMAIL } from 'constants/Constants';
import { parsePolicies } from 'utils/fm';

const SignupStart = memo(({ t, onStep, active }) => {
    const [loading, setLoading] = useState(false);
    const policies = useRef(
        parsePolicies({
            digitsMinNumber: '1',
            lowerCaseLettersMinNumber: '1',
            maxLength: '30',
            minLength: '10',
            regexDigits: '^(?=(.*\\d){1})',
            regexLowerCase: '^(?=(.*[a-z]){1})',
            regexSpecial:
                '^(?=(.*[ !@#$%^\u0026*()_\\+\\-=\\[\\]{};\':"\\|,.\u003c\u003e\\/?]){1})',
            regexUpperCase: '^(?=(.*[A-Z]){1})',
            requireDigits: 'True',
            requireLowerCaseLetters: 'True',
            requireSpecialLetters: 'True',
            uppercaseLettersMin: 'True',
            specialLettersMinNumber: '1',
            upperCaseLettersMinNumber: '1',
        }),
    );
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState();
    const onChange = (field) => (value) => {
        if (errors) setErrors({ ...errors, [field]: null });
        setForm({ ...form, [field]: value });
    };

    const onContinue = useCallback(() => {
        let newErrors = {};

        // Requireds
        if (!form.name) newErrors.name = t('label_field_required');
        if (!form.email) newErrors.email = t('label_field_required');
        if (!form.password) newErrors.password = t('label_field_required');

        // Error validation
        if (form.email && !form.email.trim().match(REG_EX_EMAIL))
            newErrors.email = t('error_validation_email');

        if (
            !newErrors.password &&
            !policies.current.every((policy) => policy.condition(form.password))
        ) {
            newErrors.password = t('error_password_policies');
        }

        if (Object.keys(newErrors)?.length) setErrors(newErrors);
        else {
            setLoading(true);
            Context.domainManager
                .checkUserExists({ email: form.email })
                .then((res) => {
                    if (res.Result) {
                        setErrors({
                            email: t('confirm_exists_email_creating_account'),
                        });
                    } else {
                        setErrors(null);
                        onStep(1, form);
                    }
                })
                .catch((e) => {
                    console.error(e);
                    onStep(4);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [form, onStep, t]);

    useEffect(() => {
        if (active) {
            Tracking.logEvent({
                event: 'signup',
                functionality: 'signupStarted',
                platform: 'web',
            });
        }
    }, [active]);

    useEffect(() => {
        if (window.top !== window.self && window.parent) {
            window.parent.postMessage(
                {
                    sender: 'signup_send_email',
                },
                '*',
            );
            window.addEventListener('message', (event) => {
                const email = event?.data?.email;
                if (event?.data?.sender === 'signup_email' && email && email !== 'undefined') {
                    setForm({
                        email,
                    });
                }
            });
        }
    }, []);

    return (
        <div className="signup__start">
            <Text type="h6" isHighlighted>
                {t('label_signup_start_title')}
            </Text>
            <form>
                <Input
                    label={t('label_signup_fullname')}
                    placeholder={t('label_signup_fullname_placeholder')}
                    onChange={onChange('name')}
                    onEnter={onContinue}
                    value={form.name}
                    type="text"
                    isRequired
                    isFullWidth
                    autoFocus={true}
                    error={errors?.name}
                />
                <Input
                    label={t('label_signup_email')}
                    placeholder={t('you@example.com')}
                    onChange={onChange('email')}
                    onEnter={onContinue}
                    value={form.email}
                    type="email"
                    isRequired
                    isFullWidth
                    error={errors?.email}
                />
                <Password
                    label={t('label_signup_password')}
                    placeholder={t('label_signup_password_placeholder')}
                    onChange={onChange('password')}
                    onEnter={onContinue}
                    value={form.password}
                    isRequired
                    isFullWidth
                    error={errors?.password}
                />
                <PasswordPolicies
                    className="signup__password-policies"
                    password={form.password}
                    policies={policies.current}
                    t={t}
                />
            </form>
            <footer>
                <Link
                    className="signup__link"
                    variation="primary"
                    href="https://app.forcemanager.net/#/login"
                >
                    {t('label_signup_kit_digital')}
                </Link>
                <Button size="big" onClick={onContinue} isLoading={loading}>
                    {t('action_next')}
                </Button>
            </footer>
        </div>
    );
});

export default SignupStart;
